import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

export default function IssueSearch({ onIssueSelect }) {
  const { user, session } = useAuth();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchInputRef = useRef(null);
  const resultsRefs = useRef([]);

  // Get provider from user metadata
  const provider = user?.app_metadata?.provider;
  const providerToken = session?.provider_token;

  // Check if user is authenticated with GitHub or GitLab
  const isAuthenticated = provider && (provider === 'github' || provider === 'gitlab') && providerToken;

  const searchIssues = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a search query');
      return;
    }

    setIsSearching(true);
    setError(null);
    setSearchResults([]);

    try {
      let issues = [];

      if (provider === 'github') {
        // GitHub API: Search all issues directly
        const username = user.user_metadata.user_name || user.user_metadata.preferred_username;
        const query = `${searchQuery} in:title type:issue involves:${username} is:open`;

        const response = await fetch(
          `https://api.github.com/search/issues?q=${encodeURIComponent(query)}&sort=updated&per_page=10`,
          {
            headers: {
              'Authorization': `token ${providerToken}`,
              'Accept': 'application/vnd.github.v3+json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`GitHub API error: ${response.status}`);
        }

        const data = await response.json();

        issues = data.items.map(issue => ({
          id: issue.id,
          number: issue.number,
          title: issue.title,
          url: issue.html_url,
          state: issue.state,
          repository: issue.repository_url.split('/').slice(-2).join('/'),
          platform: 'github',
          updated_at: issue.updated_at
        }));

      } else if (provider === 'gitlab') {
        // GitLab API: Search all issues directly
        const scope = assignedToMe ? 'assigned_to_me' : 'created_by_me';
        const response = await fetch(
          `https://gitlab.com/api/v4/issues?search=${encodeURIComponent(searchQuery)}&scope=${scope}&state=opened&per_page=10&order_by=updated_at`,
          {
            headers: {
              'Authorization': `Bearer ${providerToken}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`GitLab API error: ${response.status}`);
        }

        const data = await response.json();

        issues = data.map(issue => ({
          id: issue.id,
          number: issue.iid,
          title: issue.title,
          url: issue.web_url,
          state: issue.state,
          repository: issue.references.full.split('#')[0],
          platform: 'gitlab',
          updated_at: issue.updated_at
        }));
      }

      setSearchResults(issues);
      setShowResults(true);
      setSelectedIndex(-1);
    } catch (err) {
      console.error('Error searching issues:', err);
      setError(err.message || 'Failed to search issues. Please check your connection and try again.');
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (selectedIndex >= 0 && resultsRefs.current[selectedIndex]) {
      resultsRefs.current[selectedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [selectedIndex]);

  const handleIssueSelect = (issue) => {
    // Call the parent component's callback with the selected issue
    onIssueSelect({
      url: issue.url,
      title: issue.title,
      number: issue.number,
      platform: issue.platform
    });
    // Clear search
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showResults || searchResults.length === 0) {
      if (e.key === 'Escape' && showResults) {
        setShowResults(false);
        setSelectedIndex(-1);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        if (selectedIndex <= 0) {
          searchInputRef.current?.focus();
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && searchResults[selectedIndex]) {
          handleIssueSelect(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowResults(false);
        setSelectedIndex(-1);
        searchInputRef.current?.focus();
        break;
      default:
        break;
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      searchIssues();
    } else if (e.key === 'ArrowDown' && showResults && searchResults.length > 0) {
      e.preventDefault();
      setSelectedIndex(0);
      resultsRefs.current[0]?.focus();
    } else if (e.key === 'Escape') {
      setShowResults(false);
      setSelectedIndex(-1);
    }
  };

  if (!isAuthenticated) {
    return null; // Don't show if not authenticated with GitHub/GitLab
  }

  return (
    <div className="space-y-3">
      {/* GitLab Scope Toggle */}
      {provider === 'gitlab' && (
        <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <label className="flex items-center cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                checked={assignedToMe}
                onChange={(e) => setAssignedToMe(e.target.checked)}
                className="sr-only"
                aria-label={assignedToMe ? t('issueSearch.assignedToMe') : t('issueSearch.createdByMe')}
              />
              <div className={`block w-10 h-6 rounded-full transition-colors ${assignedToMe ? 'bg-orange-600' : 'bg-gray-400 dark:bg-gray-600'}`}></div>
              <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${assignedToMe ? 'transform translate-x-4' : ''}`}></div>
            </div>
            <div className="ml-3 text-sm">
              <span className="font-medium text-gray-900 dark:text-white">
                {assignedToMe ? t('issueSearch.assignedToMe') : t('issueSearch.createdByMe')}
              </span>
            </div>
          </label>
        </div>
      )}

      <div className="flex gap-2">
        <div className="flex-1">
          <label htmlFor="issue-search-input" className="sr-only">
            {t('accessibility.searchIssues', `Search your ${provider === 'github' ? 'GitHub' : 'GitLab'} issues`)}
          </label>
          <input
            id="issue-search-input"
            ref={searchInputRef}
            type="text"
            role="combobox"
            aria-expanded={showResults && searchResults.length > 0}
            aria-controls="issue-search-results"
            aria-autocomplete="list"
            aria-activedescendant={selectedIndex >= 0 ? `issue-result-${selectedIndex}` : undefined}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder={`Search your ${provider === 'github' ? 'GitHub' : 'GitLab'} issues...`}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            disabled={isSearching}
          />
        </div>
        <button
          onClick={searchIssues}
          disabled={isSearching || !searchQuery.trim()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          aria-label={t('accessibility.searchButton', 'Search issues')}
        >
          {isSearching ? (
            <>
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Searching...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Search
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {showResults && searchResults.length > 0 && (
        <div
          id="issue-search-results"
          role="listbox"
          aria-label={t('accessibility.issueSearchResults', 'Issue search results')}
          className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden max-h-96 overflow-y-auto"
        >
          <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b border-gray-300 dark:border-gray-600">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300" role="status">
              Found {searchResults.length} issue{searchResults.length !== 1 ? 's' : ''}
            </p>
          </div>
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {searchResults.map((issue, index) => (
              <li
                key={issue.id}
                id={`issue-result-${index}`}
                ref={(el) => (resultsRefs.current[index] = el)}
                role="option"
                aria-selected={selectedIndex === index}
                tabIndex={0}
                onClick={() => handleIssueSelect(issue)}
                onKeyDown={handleKeyDown}
                className={`p-4 cursor-pointer transition-colors ${
                  selectedIndex === index
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                        issue.platform === 'github'
                          ? 'bg-gray-800 text-white dark:bg-gray-600'
                          : 'bg-orange-600 text-white'
                      }`}>
                        {issue.platform === 'github' ? 'GitHub' : 'GitLab'}
                      </span>
                      <span className="text-sm font-mono text-gray-600 dark:text-gray-400">
                        {issue.repository} #{issue.number}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        issue.state === 'open' || issue.state === 'opened'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                      }`}>
                        {issue.state}
                      </span>
                    </div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-1 line-clamp-2">
                      {issue.title}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Updated {new Date(issue.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showResults && searchResults.length === 0 && !isSearching && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            No issues found. Try a different search query.
          </p>
        </div>
      )}
    </div>
  );
}
